/** Loose shapes for platform token / preference responses (host `platformApi` is untyped at the boundary). */

export type ApiObject = Record<string, unknown>;

export type RevealTokenResponse = ApiObject & {
  message?: string;
  tokenType?: string;
  amount?: number;
  currency?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  multiuse?: boolean;
  conditions?: { redeemer?: { name?: string } };
};

const UNUSABLE_CASH_TOKEN_STATUSES = new Set([
  'redeemed',
  'completed',
  'expired',
  'cancelled',
  'canceled',
  'used',
  'spent',
  'void',
]);

export type VerifiedCashToken = {
  amount: number;
  currency: string;
  status?: string;
};

export type CashTokenVerifyResult =
  | { ok: true; token: VerifiedCashToken }
  | { ok: false; errorKey: string; params?: Record<string, string> };

/**
 * Reveal payload must itself prove the code is a usable cash token.
 * Do not infer type, amount, or currency from the invoice or from `message`.
 */
export function verifyRevealedCashToken(
  revealed: RevealTokenResponse | null | undefined,
  invoiceCurrency: string
): CashTokenVerifyResult {
  if (!revealed || typeof revealed !== 'object') {
    return { ok: false, errorKey: 'redeemToken.applyCashTokenFailed' };
  }
  if (revealed.tokenType !== 'cash') {
    return { ok: false, errorKey: 'redeemToken.applyCashTokenNotCash' };
  }
  const status = typeof revealed.status === 'string' ? revealed.status.toLowerCase() : undefined;
  if (status && UNUSABLE_CASH_TOKEN_STATUSES.has(status)) {
    return { ok: false, errorKey: 'redeemToken.applyCashTokenUnusable' };
  }
  if (typeof revealed.amount !== 'number' || !Number.isFinite(revealed.amount) || revealed.amount <= 0) {
    return { ok: false, errorKey: 'redeemToken.applyCashTokenMissingAmount' };
  }
  const currency = typeof revealed.currency === 'string' ? revealed.currency.trim() : '';
  if (!currency) {
    return { ok: false, errorKey: 'redeemToken.applyCashTokenMissingCurrency' };
  }
  if (currency.toUpperCase() !== invoiceCurrency.toUpperCase()) {
    return {
      ok: false,
      errorKey: 'redeemToken.applyCashTokenCurrency',
      params: { tokenCurrency: currency, invoiceCurrency },
    };
  }
  return { ok: true, token: { amount: revealed.amount, currency, status: revealed.status } };
}

export type RedemptionApiResult = ApiObject & {
  error?: string;
  status?: string;
  message?: string;
  amount?: number;
  balance?: number;
  currency?: string;
  /** Backend swallows invoice-pay failures and returns this instead of throwing. */
  unableToPayInvoice?: boolean;
  paidInvoice?: string;
  success?: boolean;
  paymentAmount?: number;
  remainingAmount?: number;
  invoiceStatus?: string;
  transactionId?: string;
  details?: ApiObject & {
    amount?: number;
    currency?: string;
    status?: string;
    remainder?: number;
    transactionFee?: number;
    newBalance?: number;
    paidInvoice?: string;
    message?: unknown;
    invoicePayment?: unknown;
    invoiceRemainder?: number;
    invoiceStatus?: string;
  };
};

export type InvoicePaymentPrefs = {
  mode?: string;
  threshold?: number;
};

export type AutoDepositPrefs = {
  autoDepositEnabled?: boolean;
  autoDepositThreshold?: number;
};

export type MerchantProfileForRedeem = ApiObject & {
  preferences?: { autoDeposit?: boolean };
  guestCheckoutNotifications?: { autoDeposit?: boolean };
};

function parseMoney(value: unknown): number | undefined {
  if (value === 0 || value === '0') return 0;
  if (value == null || value === '') return undefined;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : undefined;
}

function roundMoney(value: number): number {
  return Math.round(value * 100) / 100;
}

function firstPositiveMoney(candidates: unknown[]): number | undefined {
  for (const value of candidates) {
    const n = parseMoney(value);
    if (n !== undefined && n > 0) return n;
  }
  return undefined;
}

/**
 * Invoice leftover after a pay.
 * Cash-token redeem often returns remainder/remainingAmount 0; that is not the invoice leftover.
 * When due-before-pay and paid are known, those win over a 0 API remainder.
 */
export function leftoverAfterInvoicePay(
  result: ApiObject | null | undefined,
  dueBeforePay?: unknown,
  paidAmount?: unknown
): number | undefined {
  const due = parseMoney(dueBeforePay);
  const paid = parseMoney(paidAmount);
  const fromDue =
    due != null && paid != null ? roundMoney(Math.max(0, due - paid)) : undefined;

  const details =
    result?.details && typeof result.details === 'object'
      ? (result.details as ApiObject)
      : {};
  const fromInvoiceFields = firstPositiveMoney([
    result?.remainingAmount,
    result?.invoiceRemainder,
    details.invoiceRemainder,
  ]);

  if (fromDue != null && fromDue > 0) return fromInvoiceFields ?? fromDue;
  if (fromInvoiceFields != null) return fromInvoiceFields;
  if (fromDue === 0) return 0;

  return firstPositiveMoney([result?.remainder, details.remainder]) ?? fromDue;
}

export function invoicePayRemainder(
  result: ApiObject | null | undefined,
  dueBeforePay?: unknown,
  paidAmount?: unknown
): number | undefined {
  return leftoverAfterInvoicePay(result, dueBeforePay, paidAmount);
}

export function isPartialInvoicePay(
  result: ApiObject | null | undefined,
  dueBeforePay?: unknown,
  paidAmount?: unknown
): boolean {
  const remainder = leftoverAfterInvoicePay(result, dueBeforePay, paidAmount);
  return typeof remainder === 'number' && remainder > 0;
}
